/**
 * Auth Service Factory
 *
 * Returns the Okta authentication service.
 *
 * This factory pattern allows for future authentication provider flexibility
 * without changing application code.
 *
 * Note: Microsoft Entra support was removed. If needed in the future, see the
 * archive/entra-implementation branch for the previous implementation.
 */

import { IAuthService } from '../../types/auth';
// Okta service - platform-specific files (.web.ts / .native.ts)
// Webpack/Metro will automatically resolve the correct one
import { oktaAuthService } from './oktaAuthService';

type AuthProvider = 'okta';

let cachedService: IAuthService | null = null;

/**
 * Get the configured auth provider from environment
 */
function getAuthProvider(): AuthProvider {
  // Always use Okta authentication
  return 'okta';
}

/**
 * Get the appropriate authentication service based on configuration
 *
 * @returns {IAuthService} The configured authentication service
 *
 * @example
 * ```typescript
 * // In your component or context
 * const authService = getAuthService();
 * await authService.login();
 * ```
 */
export function getAuthService(): IAuthService {
  // Return cached service if available (singleton pattern)
  if (cachedService) {
    return cachedService;
  }

  const provider = getAuthProvider();

  // Using Okta authentication service (platform-specific)

  // Cache the service instance
  cachedService = oktaAuthService;

  return oktaAuthService;
}

/**
 * Get the currently configured auth provider name
 * Useful for debugging or displaying provider info to users
 */
export function getCurrentProvider(): AuthProvider {
  return getAuthProvider();
}

/**
 * Reset the cached service (useful for testing)
 * @internal
 */
export function resetAuthServiceCache(): void {
  cachedService = null;
}

// Export for testing/debugging
export const _getAuthProvider = getAuthProvider;
